// The arithmetic every chart in this directory shares. Roughly what
// `Sparkline.tsx` inlines, factored out once six callers needed it — and no
// further: this is not a scale library, it is the four functions the drawings
// here actually use.
//
// Everything is pure and exported so it can be unit-tested directly.
// `format.test.ts:119-143` is the precedent: assert the geometry, never the
// rendered SVG path string, or the test pins the drawing instead of the rule.

/** `[0, max] -> [0, size]`, anchored at ZERO, never at the minimum.
 *
 * Baseline-at-zero is a decision, not an oversight, and it is the same one
 * `Sparkline` makes: these are counts and rates whose distance from zero is
 * the point, and a min-anchored axis exaggerates a flat series into a
 * dramatic one. A chart that wants a min-anchored axis has to say so by not
 * using this.
 */
export function linear(max: number, size: number): (value: number) => number {
  // `|| 1` guards an all-zero series against a divide-by-zero that would
  // render every mark at NaN — silently, as an empty chart.
  const top = Math.max(max, 0) || 1;
  return (value) => (value / top) * size;
}

export type Band = {
  /** Left edge of slot `index`. */
  start: (index: number) => number;
  /** Width of one slot, gap already subtracted. */
  width: number;
  /** Centre of slot `index` — where a label or a tick belongs. */
  center: (index: number) => number;
};

/** `count` evenly spaced slots across `size`, with `gap` between them. */
export function band(count: number, size: number, gap = 0): Band {
  const step = count > 0 ? size / count : size;
  const width = Math.max(0, step - gap);
  return {
    start: (index) => index * step + gap / 2,
    width,
    center: (index) => index * step + step / 2,
  };
}

/** Up to `count` round-ish tick values across `[0, max]`, always including 0.
 *
 * Returns `[0]` for an empty or zero-topped domain rather than an axis of
 * fabricated gridlines — there is nothing to grade.
 */
export function ticks(max: number, count = 4): number[] {
  if (!(max > 0)) return [0];
  const raw = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? magnitude * 10;
  const out: number[] = [];
  for (let value = 0; value <= max + step / 2; value += step) out.push(value);
  return out;
}

/** The largest value in a series, ignoring gaps. `0` when nothing is measured.
 *
 * Takes `null` as a GAP, not a zero — the rule the whole directory is built
 * on. A caller that coerces its nulls before calling this has already lost the
 * distinction and will draw an unmeasured day flat along the axis.
 */
export function maxOf(values: Array<number | null | undefined>): number {
  let top = 0;
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (value > top) top = value;
  }
  return top;
}
